import type { ReactNode } from 'react';
import { useState } from 'react';

import {
  Button,
  type DateRangeValue,
  DropdownList,
  type DropdownListOption,
  Icon,
  type Key,
  RangeCalendarPopover,
  SearchableDropdownList,
  type SearchableDropdownListProps,
  RACSelect as Select,
  VisuallyHidden,
} from '@iconik-io/uad';

import styles from './filter_select.module.css';

type FilterSelectProps = {
  label: string;
  className?: string;
  variant?: 'multi_select' | 'date_range';
  options?: DropdownListOption[];
  selectedKeys?: Key[];
  selectedCount?: number;
  searchable?: boolean;
  isLoading?: boolean;
  presets?: 'past' | 'future';
  onSearchValueChange?: (value: string) => void;
  filter?: SearchableDropdownListProps['filter'];
  renderOption?: (option: DropdownListOption) => ReactNode;
  onSelectionChange?: (keys: string[]) => void;
  onClearSelection?: () => void;
  dateRangeValue?: DateRangeValue | null;
  onDateRangeChange?: (range: DateRangeValue | null) => void;
};

export function FilterSelect(props: FilterSelectProps) {
  const {
    label,
    className,
    variant = 'multi_select',
    options = [],
    selectedKeys = [],
    selectedCount = 0,
    searchable = true,
    isLoading,
    presets,
    onSearchValueChange,
    filter,
    renderOption,
    onSelectionChange,
    onClearSelection,
    dateRangeValue,
    onDateRangeChange,
  } = props;
  const [searchValue, setSearchValue] = useState('');
  const dateSelectedCount = dateRangeValue ? 1 : 0;

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    onSearchValueChange?.(value);
  };

  if (variant === 'date_range') {
    return (
      <RangeCalendarPopover
        presets={presets}
        value={dateRangeValue ?? null}
        onChange={(nextValue) => onDateRangeChange?.(nextValue)}
        onClear={onClearSelection}
        canClear={Boolean(onClearSelection)}
      >
        <Button size="s" variant="tertiary">
          {label}
          {dateSelectedCount > 0 && (
            <>
              <span aria-hidden className={styles.selected_count}>
                {dateSelectedCount}
              </span>
              <VisuallyHidden>, {dateSelectedCount} selected</VisuallyHidden>
            </>
          )}
          <Icon name="chevron_small_isolated_s" size="s" />
        </Button>
      </RangeCalendarPopover>
    );
  }

  return (
    <Select<DropdownListOption, 'multiple'>
      aria-label={label}
      selectionMode="multiple"
      allowsEmptyCollection
      value={selectedKeys}
      onChange={(keys) => onSelectionChange?.(keys.map(String))}
      onOpenChange={(isOpen) => {
        if (searchable && !isOpen) {
          handleSearchValueChange('');
        }
      }}
    >
      <Button size="s" variant="tertiary">
        {label}
        {selectedCount > 0 && (
          <>
            <span aria-hidden className={styles.selected_count}>
              {selectedCount}
            </span>
            <VisuallyHidden>, {selectedCount} selected</VisuallyHidden>
          </>
        )}
        <Icon name="chevron_small_isolated_s" size="s" />
      </Button>
      {searchable ? (
        <SearchableDropdownList
          className={className}
          options={isLoading ? [] : options}
          selectionMode="multiple"
          loadingText="Loading..."
          searchValue={searchValue}
          onSearchValueChange={handleSearchValueChange}
          isLoading={isLoading}
          filter={filter}
          renderOption={renderOption}
          placement="bottom start"
        />
      ) : (
        <DropdownList
          className={className}
          options={isLoading ? [] : options}
          selectionMode="multiple"
          loadingText="Loading..."
          isLoading={isLoading}
          renderOption={renderOption}
          placement="bottom start"
        />
      )}
    </Select>
  );
}
